/** Lightweight runtime for data-authored, anchor-relative camera cutscenes. */
import * as THREE from "three";
import type { CutsceneAssetV1, CutsceneCameraKeyV1 } from "@hyperlight/shared";

export interface CutsceneFrame {
  /**
   * Convert [right, up, forward] from the moving anchor into RENDER space —
   * the same interpolated, floating-origin-relative space the world is drawn
   * in this frame. Anchoring to the raw sim pose instead makes the camera
   * sawtooth against the very ground it is framing, once per sim tick.
   */
  point(local: readonly [number, number, number], out: THREE.Vector3): THREE.Vector3;
  up(out: THREE.Vector3): THREE.Vector3;
}

export class CutscenePlayer {
  active = false;
  private readonly root = document.createElement("div");
  private readonly eyebrow: HTMLElement;
  private readonly title: HTMLElement;
  private asset: CutsceneAssetV1 | null = null;
  private frame: (() => CutsceneFrame) | null = null;
  private tokens: Record<string, string> = {};
  private time = 0;
  private resolve: (() => void) | null = null;
  private readonly pos = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    this.root.id = "cutscene-overlay";
    this.root.innerHTML = `<div class="cs-letter cs-top"></div><div class="cs-copy"><span></span><strong></strong></div><button type="button">SKIP</button><div class="cs-letter cs-bottom"></div>`;
    document.body.appendChild(this.root);
    this.eyebrow = this.root.querySelector("span")!;
    this.title = this.root.querySelector("strong")!;
    this.root.querySelector("button")!.addEventListener("click", () => this.finish());
  }

  play(asset: CutsceneAssetV1, frame: () => CutsceneFrame, tokens: Record<string, string>): Promise<void> {
    this.asset = asset;
    this.frame = frame;
    this.tokens = tokens;
    this.time = 0;
    this.active = true;
    // A cutscene owns the whole frame. The title screen skews the film back so
    // the world clears the login card, and the creator offsets the view around
    // its panel; either one left set here composes the entire timeline off-centre.
    this.camera.filmOffset = 0;
    this.camera.clearViewOffset();
    this.root.classList.add("visible");
    (this.root.querySelector("button") as HTMLButtonElement).hidden = !asset.skippable;
    return new Promise<void>((resolve) => { this.resolve = resolve; });
  }

  update(dt: number): void {
    if (!this.active || !this.asset || !this.frame) return;
    this.time = Math.min(this.asset.duration, this.time + dt);
    const [a, b] = this.keys(this.asset.camera, this.time);
    const span = Math.max(1e-6, b.t - a.t);
    let k = Math.max(0, Math.min(1, (this.time - a.t) / span));
    k = this.ease(k, b.ease ?? "smooth");
    const p = this.mix3(a.position, b.position, k);
    const target = this.mix3(a.target, b.target, k);
    const frame = this.frame();
    frame.point(p, this.pos);
    frame.point(target, this.target);
    this.camera.position.copy(this.pos);
    this.camera.up.copy(frame.up(this.up).normalize());
    this.camera.lookAt(this.target);
    this.camera.fov = a.fov + (b.fov - a.fov) * k;
    this.camera.updateProjectionMatrix();

    const cue = this.asset.cues.find((item) => this.time >= item.start && this.time <= item.end);
    this.root.classList.toggle("has-cue", Boolean(cue));
    this.eyebrow.textContent = cue ? this.expand(cue.eyebrow ?? "") : "";
    this.title.textContent = cue ? this.expand(cue.text) : "";
    if (this.time >= this.asset.duration) this.finish();
  }

  private keys(keys: CutsceneCameraKeyV1[], time: number): [CutsceneCameraKeyV1, CutsceneCameraKeyV1] {
    for (let i = 1; i < keys.length; i++) if (time <= keys[i].t) return [keys[i - 1], keys[i]];
    return [keys[keys.length - 1], keys[keys.length - 1]];
  }

  private mix3(a: readonly number[], b: readonly number[], k: number): [number, number, number] {
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
  }

  private ease(k: number, kind: NonNullable<CutsceneCameraKeyV1["ease"]>): number {
    if (kind === "linear") return k;
    if (kind === "ease-in") return k * k;
    if (kind === "ease-out") return 1 - (1 - k) * (1 - k);
    return k * k * (3 - 2 * k);
  }

  private expand(text: string): string {
    return text.replace(/\{([a-z]+)\}/gi, (_, key: string) => this.tokens[key] ?? "");
  }

  private finish(): void {
    if (!this.active) return;
    this.active = false;
    this.root.classList.remove("visible", "has-cue");
    this.asset = null;
    this.frame = null;
    const done = this.resolve;
    this.resolve = null;
    done?.();
  }
}
